import { useState } from "react"
import type { TelegramPreviewMedia } from "./telegramMessagePreview"
import { previewLabel, safePreviewHref } from "./presentation"

function mediaUrl(value: string | undefined): string | undefined {
  if (!value) return undefined
  if (safePreviewHref(value)) return value
  // Object URLs are owned/revoked by the caller. Never derive one from an ID.
  if (!value.startsWith("blob:")) return undefined
  const source = value.slice(5)
  return safePreviewHref(source) || /^null\/[a-zA-Z0-9-]+$/.test(source)
    ? value
    : undefined
}

function sizeLabel(size: number | undefined): string {
  if (size === undefined || !Number.isFinite(size) || size < 0) return ""
  if (size < 1024) return `${size} Б`
  if (size < 1024 * 1024) return `${Number((size / 1024).toFixed(1))} КБ`
  return `${Number((size / (1024 * 1024)).toFixed(1))} МБ`
}

function MediaItem({
  media,
  index,
}: {
  media?: TelegramPreviewMedia
  index: number
}) {
  const [failed, setFailed] = useState(false)
  const url = mediaUrl(media?.previewUrl)
  const name = previewLabel(media?.name || `Медиа ${index + 1}`)
  const fail = () => {
    setFailed(true)
    media?.onPreviewError?.()
  }
  return (
    <figure className="telegram-preview__media-item">
      {url && !failed ? (
        media?.kind === "video" ? (
          <video
            src={url}
            controls
            preload="metadata"
            playsInline
            aria-label={name}
            onError={fail}
          />
        ) : (
          <img src={url} alt={name} loading="lazy" onError={fail} />
        )
      ) : (
        <div className="telegram-preview__media-placeholder">
          <span>{name}</span>
          <small>
            {[
              media?.kind === "photo"
                ? "Фото"
                : media?.kind === "video"
                  ? "Видео"
                  : "Медиа",
              sizeLabel(media?.size),
            ]
              .filter(Boolean)
              .join(" · ")}
          </small>
          <small>
            {media?.previewError ??
              (media?.previewLoading
                ? "Загрузка предпросмотра…"
                : "Предпросмотр файла недоступен")}
          </small>
          {!media?.previewLoading && media?.onPreviewRetry && (
            <button
              type="button"
              onClick={() => {
                setFailed(false)
                media.onPreviewRetry?.()
              }}
            >
              Повторить
            </button>
          )}
        </div>
      )}
    </figure>
  )
}

export function MediaContent({
  ids,
  media = [],
}: {
  ids: readonly string[]
  media?: readonly TelegramPreviewMedia[]
}) {
  if (!ids.length) return null
  const descriptors = new Map(media.map((item) => [item.id, item]))
  return (
    <div
      className={`telegram-preview__media${ids.length > 1 ? " telegram-preview__media--album" : ""}`}
      role="group"
      aria-label={ids.length > 1 ? "Альбом" : "Медиа сообщения"}
    >
      {ids.map((id, index) => (
        <MediaItem
          key={`${index}:${id}:${descriptors.get(id)?.previewUrl ?? ""}`}
          media={descriptors.get(id)}
          index={index}
        />
      ))}
    </div>
  )
}
